import { Injectable } from '@angular/core';
import { Hub } from 'aws-amplify/utils';
import { Subject } from 'rxjs';
import { SignUpForm } from '../feature-modules/register/models/sign-up.model';
import { WindowRefService } from './window-ref.service';
import { RedirectService } from '../feature-modules/redirect/services/redirect.service';
import { environment } from '../../environments/environment';
import { SessionData } from '../models/session.model';
import { AmplifyAuthService } from './amplify-auth.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private authState: Subject<any> = new Subject<any>();

  constructor(
    private readonly windowRefService: WindowRefService,
    private readonly redirectService: RedirectService,
    private readonly amplifyAuth: AmplifyAuthService
  ) {
    Hub.listen('auth', (data) => {
      this.authState.next(data.payload.event);
    });
  }

  signUp(payload: SignUpForm, token: string): Promise<any> {
    const signUpParams = this.registerPayloadMapper(payload, token);
    return this.amplifyAuth.signUp(signUpParams);
  }

  login(username: string, password: string): Promise<any> {
    const signInParams = {
      username,
      password,
      options: {
        clientMetadata: { 'auth-session-id': sessionStorage.getItem('auth-session-id') },
      },
    };
    return this.amplifyAuth.signIn(signInParams).catch((error) => {
      if (error.name === 'UserAlreadyAuthenticatedException') {
        return this.amplifyAuth.signOut().then(() => this.amplifyAuth.signIn(signInParams));
      }
      throw error;
    });
  }

  logOut() {
    this.windowRefService.nativeWindow.FS?.event('[Auth] Log Out');
    this.amplifyAuth.signOut()
      .finally(() => (this.windowRefService.nativeWindow.location.href = '/login'));
  }

  /**
   * Retrieves current session data. In the process of doing so, this also (1) refreshes the accessToken and idToken if tokens are expired and a valid refreshToken presented, (2) fires relevant FS events, and (3) sets auth related cookies. It will redirect to login page if this for whatever reason fails.
   */
  handleCurrentSessionRetrieval(): Promise<SessionData> {
    return new Promise((resolve) => {
      this.amplifyAuth.fetchAuthSession({ forceRefresh: false })
        .then((session) => {
          const idTokenObj = session.tokens?.idToken;
          if (!idTokenObj) {
            this.windowRefService.nativeWindow.location.href = '/login';
            return;
          }
          const cid = idTokenObj.payload?.cid as string;
          const username = idTokenObj.payload?.['cognito:username'] as string;
          console.log('customer id login', cid);

          if (this.windowRefService.nativeWindow.FS) {
            this.windowRefService.nativeWindow.FS.setUserVars({ cid });
          } else {
            console.warn(`Fullstory is not enabled in ${environment.env} environment`);
          }
          const idToken = idTokenObj?.toString();
          this.redirectService.dropIdTokenToCookie(idToken);

          this.redirectService.dropSignInSessionIdToCookie(cid, username);
          this.redirectService.dropCidToCookie(cid);
          this.redirectService.dropAuthSessionIdToCookie();
          resolve({ cid, idToken });
        })
        .catch(() => {
          this.windowRefService.nativeWindow.location.href = '/login';
        });
    });
  }

  private registerPayloadMapper(rawFormData: SignUpForm, token: string) {
    let userAttributes: Record<string, string> = {};

    if (rawFormData.selectedContactMethod.type === 'phone') {
      userAttributes = {
        phone_number: `+1${rawFormData.selectedContactMethod.value.replace(/-/g, '')}`,
      };
    } else if (rawFormData.selectedContactMethod.type === 'email') {
      userAttributes = { email: rawFormData.selectedContactMethod.value };
    } else {
      throw new Error('Unknown Contact Method Type');
    }

    return {
      username: rawFormData.username,
      password: rawFormData.password,
      options: {
        userAttributes,
        validationData: {
          token,
          isDisclosureChecked: rawFormData.isDisclosureChecked.toString(),
        },
        clientMetadata: { 'auth-session-id': sessionStorage.getItem('auth-session-id') },
      },
    };
  }
}
